import { createSlice } from '@reduxjs/toolkit';
import {
  contractorApi,
  ContractorPortalInvitationItem,
  ContractorPortalInvitationsResponse,
} from '@store/rtk/contractors';
import { TableData } from '@uikit/Tables/types';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';
import { format } from 'date-fns';

type ContractorPortalInvitationsState = {
  linkedPayersTableData: TableData;
  pendingInvitationsTableData: TableData;
};

const formatInvitationDate = (value?: string | null) => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return format(parsed, 'MM/dd/yyyy');
};

const mapInvitationRow = (item: ContractorPortalInvitationItem) => ({
  id: item.id,
  payer: item.payer || item.payerName || item.payorName || item.name || '',
  state: item.state || item.stateName || '',
  date: formatInvitationDate(
    item.date ||
      item.acceptedDate ||
      item.invitationDate ||
      item.createdDate ||
      null
  ),
});

const getInvitationContent = (
  payload:
    | ContractorPortalInvitationsResponse
    | ContractorPortalInvitationItem[]
) => {
  if (Array.isArray(payload)) return payload;

  return (
    payload.content ||
    payload.pendingInvitations ||
    payload.pending ||
    payload.linkedPayers ||
    payload.linked ||
    []
  );
};

const mapTableData = (
  payload:
    | ContractorPortalInvitationsResponse
    | ContractorPortalInvitationItem[],
  requestTablePageChange: unknown
): TableData => {
  const content = getInvitationContent(payload);
  const pageablePayload = Array.isArray(payload) ? {} : payload;

  return {
    columns: [],
    rows: content.map(mapInvitationRow),
    page: pageablePayload.page ?? 0,
    size: pageablePayload.size ?? DEFAULT_PAGE_SIZE,
    totalElements: pageablePayload.totalElements ?? content.length,
    totalPages:
      pageablePayload.totalPages ??
      Math.max(
        1,
        Math.ceil(
          (pageablePayload.totalElements ?? content.length) /
            (pageablePayload.size ?? DEFAULT_PAGE_SIZE)
        )
      ),
    requestTablePageChange,
  };
};

const initialState: ContractorPortalInvitationsState = {
  linkedPayersTableData: {
    columns: [],
    rows: [],
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    totalElements: 0,
    totalPages: 0,
  },
  pendingInvitationsTableData: {
    columns: [],
    rows: [],
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    totalElements: 0,
    totalPages: 0,
  },
};

export const contractorPortalInvitationsSlice = createSlice({
  name: 'contractorPortalInvitations',
  initialState,
  reducers: {
    clearContractorPortalInvitations: (state) => {
      state.linkedPayersTableData = initialState.linkedPayersTableData;
      state.pendingInvitationsTableData =
        initialState.pendingInvitationsTableData;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        contractorApi.endpoints.getContractorPortalInvitations.matchFulfilled,
        (state, { payload }) => {
          state.pendingInvitationsTableData = mapTableData(
            payload,
            contractorApi.endpoints.getContractorPortalInvitations
          );
        }
      )
      .addMatcher(
        contractorApi.endpoints.getContractorPortalInvitationHistory
          .matchFulfilled,
        (state, { payload }) => {
          state.linkedPayersTableData = mapTableData(
            payload,
            contractorApi.endpoints.getContractorPortalInvitationHistory
          );
        }
      );
  },
});

export const { clearContractorPortalInvitations } =
  contractorPortalInvitationsSlice.actions;

export const selectContractorPortalInvitations = (state: {
  contractorPortalInvitations: ContractorPortalInvitationsState;
}) => state.contractorPortalInvitations;

export const contractorPortalInvitationsReducer =
  contractorPortalInvitationsSlice.reducer;
